import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Switch,
  TextField,
  Dialog,
  DialogContent,
  DialogActions,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import GoogleIcon from "@mui/icons-material/Google";
import LogoutIcon from "@mui/icons-material/Logout";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import TranslateIcon from "@mui/icons-material/Translate";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { useThemeMode } from "@/context/ThemeContext";
import { useNotification } from "@/context/NotificationContext";
import { useApiErrorMessage } from "@/hooks/useApiErrorMessage";

export function Account() {
  const { t, i18n } = useTranslation();
  const getErrorMessage = useApiErrorMessage();
  const navigate = useNavigate();
  const { user, logout, deleteAccount } = useAuth();
  const { mode, toggleTheme } = useThemeMode();
  const { notify } = useNotification();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleDelete = async () => {
    try {
      await deleteAccount();
      navigate("/");
    } catch (err) {
      notify(getErrorMessage(err), "error");
    }
    setDeleteOpen(false);
  };

  return (
    <Box sx={{ maxWidth: 700 }}>
      <Typography
        variant="h4"
        gutterBottom
        sx={{
          fontWeight: 700,
        }}
      >
        {t("account.title")}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          mb: 4,
        }}
      >
        {t("account.subtitle")}
      </Typography>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: 600,
            }}
          >
            {t("account.profile")}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                {t("account.email")}
              </Typography>
              <Typography
                sx={{
                  fontWeight: 500,
                }}
              >
                {user?.email}
              </Typography>
            </Box>
            <Chip
              icon={<GoogleIcon />}
              label={t("account.googleConnected")}
              color="success"
              size="small"
              variant="outlined"
            />
          </Box>
        </CardContent>
      </Card>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: 600,
            }}
          >
            {t("account.preferences")}
          </Typography>
          <List disablePadding>
            <ListItem sx={{ px: 0 }}>
              <ListItemIcon>
                <DarkModeIcon />
              </ListItemIcon>
              <ListItemText
                primary={t("account.darkMode")}
                secondary={t("account.darkModeDesc")}
              />
              <Switch checked={mode === "dark"} onChange={toggleTheme} />
            </ListItem>
            <ListItem sx={{ px: 0 }}>
              <ListItemIcon>
                <TranslateIcon />
              </ListItemIcon>
              <ListItemText
                primary={t("account.language")}
                secondary={t("account.languageDesc")}
              />
              <Box sx={{ display: "flex", gap: 0.5 }}>
                <Button
                  size="small"
                  variant={i18n.language === "en" ? "contained" : "outlined"}
                  onClick={() => i18n.changeLanguage("en")}
                >
                  EN
                </Button>
                <Button
                  size="small"
                  variant={i18n.language === "hr" ? "contained" : "outlined"}
                  onClick={() => i18n.changeLanguage("hr")}
                >
                  HR
                </Button>
              </Box>
            </ListItem>
          </List>
        </CardContent>
      </Card>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: 600,
            }}
          >
            {t("account.security")}
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontWeight: 500,
                }}
              >
                {t("account.signOut")}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                }}
              >
                {t("account.signOutDesc")}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="error"
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
            >
              {t("account.signOut")}
            </Button>
          </Box>
        </CardContent>
      </Card>
      <Card sx={{ border: "1px solid", borderColor: "error.main" }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <WarningAmberIcon color="error" />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "error.main",
              }}
            >
              {t("account.deleteTitle")}
            </Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mb: 2,
            }}
          >
            {t("account.deleteDesc")}
          </Typography>
          <Button
            variant="contained"
            color="error"
            onClick={() => setDeleteOpen(true)}
          >
            {t("account.deleteButton")}
          </Button>
        </CardContent>
      </Card>
      <Dialog
        open={deleteOpen}
        onClose={() => {
          setDeleteOpen(false);
          setConfirmText("");
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              fontWeight: 700,
            }}
          >
            {t("account.deleteTitle")}
          </Typography>
          <Alert severity="error" sx={{ mb: 2 }}>
            {t("account.deleteDesc")}
          </Alert>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t("account.deleteConfirm")}
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder={t("account.deletePhrase")}
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteOpen(false);
              setConfirmText("");
            }}
          >
            {t("account.cancel")}
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={confirmText !== t("account.deletePhrase")}
            onClick={handleDelete}
          >
            {t("account.deleteButton")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
